import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import PropTypes from 'prop-types'
import { CiCircleChevDown, CiCircleChevUp } from 'react-icons/ci'
import { FaPlay } from 'react-icons/fa'
import { cardHover, hoverScale } from '../../utils/animations'

const PortfolioItem = ({ data }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [videoPlaying, setVideoPlaying] = useState(false)

  // If videoId exists → use YouTube, else fall back to image
  const hasVideo = !!data.videoId
  const thumbnailUrl = hasVideo
    ? `https://img.youtube.com/vi/${data.videoId}/maxresdefault.jpg`
    : null

  return (
    <motion.article
      className="portfolio__item"
      variants={cardHover}
      whileHover="hover"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      {/* ── IMAGE or YOUTUBE VIDEO ── */}
      <div className="portfolio__item-image">
        {hasVideo ? (
          videoPlaying ? (
            // Actual embedded player (like your screenshot)
            <iframe
              src={`https://www.youtube.com/embed/${data.videoId}?autoplay=1&rel=0&modestbranding=1`}
              title={data.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{ width: '100%', height: '100%', borderRadius: '1.2rem' }}
            />
          ) : (
            // Thumbnail + Play button overlay
            <div
              className="portfolio__yt-thumb"
              onClick={() => setVideoPlaying(true)}
            >
              <img src={thumbnailUrl} alt={data.title} />
              <div className="portfolio__yt-overlay">
                <motion.div
                  className="portfolio__yt-play"
                  whileHover={{ scale: 1.15 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FaPlay />
                </motion.div>
              </div>
            </div>
          )
        ) : (
          // Fallback: regular image for projects without videoId
          <img src={data.image} alt={data.title} />
        )}
      </div>

      {/* ── TITLE + TOGGLE ── */}
      <h3 className="portfolio__item-title">
        <div className="portfolio__item-title__text">{data.title}</div>
        <motion.button
          className="portfolio__dropdown"
          onClick={() => setIsExpanded(!isExpanded)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          <span className="portfolio__dropdown__icon">
            {isExpanded ? <CiCircleChevUp /> : <CiCircleChevDown />}
          </span>
          <span className="portfolio__dropdown__text">
            {isExpanded ? 'Less' : 'More'}
          </span>
        </motion.button>
      </h3>

      {/* ── EXPANDABLE DETAILS ── */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            <div className="portfolio__item__description active">
              <p>{data.description}</p>
            </div>
            <div className="portfolio__item__tools active">
              <h4>Tools & Technologies</h4>
              <div className="portfolio__item__tools__list">
                {data.tools_tech.map((tool, index) => (
                  <motion.div
                    key={tool}
                    className="portfolio__item__tool"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.07 }}
                  >
                    {tool}
                  </motion.div>
                ))}
              </div>
            </div>
            <div className="portfolio__item__features active">
              <h4>Features</h4>
              <div className="portfolio__item__features__list">
                {data.features.map((feature, index) => (
                  <motion.div
                    key={feature}
                    className="portfolio__item__feature"
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.07 }}
                  >
                    <span className="portfolio__feature-dot" />
                    {feature}
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── CTA BUTTONS ── */}
      <div className="portfolio__item-cta">
        <motion.a
          href={data.github}
          className="btn"
          target="_blank"
          rel="noreferrer"
          variants={hoverScale}
          whileHover="hover"
          whileTap="tap"
        >
          Github
        </motion.a>
        {data.live !== '#' && (
          <motion.a
            href={data.live}
            className="btn btn-primary"
            target="_blank"
            rel="noreferrer"
            variants={hoverScale}
            whileHover="hover"
            whileTap="tap"
          >
            Live
          </motion.a>
        )}
      </div>
    </motion.article>
  )
}

PortfolioItem.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.number.isRequired,
    image: PropTypes.string,
    videoId: PropTypes.string,        // ← new optional field
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    tools_tech: PropTypes.arrayOf(PropTypes.string).isRequired,
    features: PropTypes.arrayOf(PropTypes.string).isRequired,
    github: PropTypes.string.isRequired,
    live: PropTypes.string
  }).isRequired
}

export default PortfolioItem